import React from "react";

function clamp(n, a, b) {
  return Math.min(b, Math.max(a, n));
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(!!mq.matches);
    update();

    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  return reduced;
}

function usePageVisibility() {
  const [visible, setVisible] = React.useState(
    typeof document !== "undefined" ? document.visibilityState === "visible" : true
  );

  React.useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  return visible;
}

function useThemeMode() {
  const [isLight, setIsLight] = React.useState(false);

  React.useEffect(() => {
    const root = document.documentElement;
    const update = () => setIsLight(root.classList.contains("theme-light"));
    update();

    const mo = new MutationObserver(update);
    mo.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  return isLight;
}

function readThemeColors(isLight) {
  const cs = getComputedStyle(document.documentElement);
  const steel = cs.getPropertyValue("--steel-0").trim() || "#7C8CA6";
  const offwhite = cs.getPropertyValue("--offwhite").trim() || "#E9EEF6";
  const bg = isLight
    ? cs.getPropertyValue("--light-bg-0").trim() || "#F6F8FB"
    : cs.getPropertyValue("--night-0").trim() || "#0E1116";

  return { steel, offwhite, bg };
}

/**
 * Style A (final): Market Pulse Grid
 * - Subtle grid lines
 * - Faint sparkline updates
 * - Occasional tick dots
 *
 * Intensity is tuned per theme:
 * - Dark: slightly higher for visibility
 * - Light: lower to protect readability
 */
export default function GlobalBackgroundAnimation() {
  const reduced = usePrefersReducedMotion();
  const visible = usePageVisibility();
  const isLight = useThemeMode();

  // Tuned intensity (0..1). Kept subtle but clearly visible.
  // Dark needs a bit more contrast; light stays conservative.
  // Visibility-tuned intensity. Still subtle, but clearly perceptible on typical laptop displays.
  const intensity = isLight ? 0.26 : 0.46;

  const canvasRef = React.useRef(null);
  const seriesRef = React.useRef(null);
  const ticksRef = React.useRef([]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const shouldRun = !reduced && visible && intensity > 0.001;

    // If animation is disabled (e.g., prefers-reduced-motion), we still render a
    // static finance-themed background so the site doesn't look "empty".
    // This keeps compliance (no animation) while preserving the design.

    const DPR = Math.min(2, window.devicePixelRatio || 1);
    let raf = 0;
    let lastT = performance.now();

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.floor(w * DPR);
      canvas.height = Math.floor(h * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };

    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    function drawOnce(t) {
      const dt = Math.min(64, t - lastT || 16);
      lastT = t;

      const { offwhite } = readThemeColors(isLight);

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      ctx.clearRect(0, 0, w, h);

      // subtle drifting grid
      const step = 84;
      const gridAlpha = (isLight ? 0.075 : 0.11) * intensity;
      ctx.lineWidth = 1;
      ctx.strokeStyle = `rgba(124, 140, 166, ${gridAlpha})`;
      const ox = (t / 80) % step;
      const oy = (t / 110) % step;
      for (let x = -step; x < w + step; x += step) {
        ctx.beginPath();
        ctx.moveTo(x + ox, 0);
        ctx.lineTo(x + ox, h);
        ctx.stroke();
      }
      for (let y = -step; y < h + step; y += step) {
        ctx.beginPath();
        ctx.moveTo(0, y + oy);
        ctx.lineTo(w, y + oy);
        ctx.stroke();
      }

      // initialize sparkline
      if (!seriesRef.current) {
        seriesRef.current = Array.from({ length: 62 }).map(
          (_, i) => 0.55 + 0.04 * Math.sin(i / 6)
        );
      }

      // update series slowly
      if (t % 95 < dt) {
        const s = seriesRef.current;
        const prev = s[s.length - 1];
        const noise = (Math.random() - 0.5) * 0.05 * intensity;
        const next = clamp(prev + noise, 0.18, 0.86);
        seriesRef.current = [...s.slice(1), next];

        // occasionally spawn a tick dot
        if (Math.random() < (isLight ? 0.11 : 0.16) * intensity) {
          ticksRef.current.push({
            x: Math.random() * w,
            y: Math.random() * h,
            born: t,
            life: 1000 + Math.random() * 1200,
          });
        }
      }

      // sparkline (faint)
      const s = seriesRef.current;
      const lineAlpha = (isLight ? 0.09 : 0.14) * intensity;
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = `rgba(124, 140, 166, ${lineAlpha})`;
      ctx.beginPath();
      s.forEach((v, i) => {
        const x = (i / (s.length - 1)) * w;
        const y = h * 0.60 + (0.5 - v) * (h * 0.42);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();

      // ticks (tiny pulsing dots)
      const keep = [];
      for (const tk of ticksRef.current) {
        const age = t - tk.born;
        if (age > tk.life) continue;
        keep.push(tk);

        const pulse = 0.5 + 0.5 * Math.sin(age / 160);
        const a = (0.02 + 0.07 * pulse) * intensity;
        ctx.fillStyle = `rgba(124, 140, 166, ${a})`;
        ctx.beginPath();
        ctx.arc(tk.x, tk.y, 1.0 + 1.2 * pulse, 0, Math.PI * 2);
        ctx.fill();
      }
      ticksRef.current = keep;

      // tiny highlight point
      const ly = h * 0.60 + (0.5 - s[s.length - 1]) * (h * 0.42);
      ctx.fillStyle = `rgba(233, 238, 246, ${0.05 * intensity})`;
      ctx.beginPath();
      ctx.arc(w - 7, ly, 1.4, 0, Math.PI * 2);
      ctx.fill();

      // readability veil (kept very subtle)
      ctx.fillStyle = isLight
        ? `rgba(246, 248, 251, ${0.01 + 0.02 * intensity})`
        : `rgba(14, 17, 22, ${0.03 + 0.03 * intensity})`;
      ctx.fillRect(0, 0, w, h);

      // ultra-faint offwhite noise line near bottom (adds premium texture)
      ctx.strokeStyle = `rgba(233, 238, 246, ${0.02 * intensity})`;
      ctx.beginPath();
      ctx.moveTo(0, h * 0.86);
      ctx.lineTo(w, h * 0.86);
      ctx.stroke();

      if (shouldRun) raf = requestAnimationFrame(drawOnce);
    }

    // Draw at least once even if animation is disabled
    raf = requestAnimationFrame(drawOnce);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [reduced, visible, isLight, intensity]);

  // Render behind all content
  return (
    <div
      className="global-bg-wrap"
      aria-hidden="true"
      style={{ opacity: intensity > 0 ? 1 : 0 }}
    >
      <canvas ref={canvasRef} className="global-bg-canvas" />
      <div className="global-bg-overlay" />
    </div>
  );
}
